import React, { useState, forwardRef, useEffect, useMemo, cloneElement, useCallback } from 'react'
import { usePaletteTheme } from '../../context/paletteTheme'
import { styled } from '@mui/material/styles'
import { Done, Search } from '@mui/icons-material'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import PopoverMenu from '../ui/menuPopover/MenuPopover'
import { Divider, InputBase } from '@mui/material'
import { capitalize } from '../../utils/tools'
import styles from './AutocomplateButton.module.scss'

const StyledAutocompletePopper = styled('div')(({colors}) => ({
    [`& .${autocompleteClasses.paper}`]: {
        boxShadow: 'none',
        backgroundColor: 'unset',
        border: 'unset',
        margin: 0,
    },
    [`& .${autocompleteClasses.listbox}`]: {
        padding: 0,
        [`& .${autocompleteClasses.option}`]: {
            color: colors.textSecondary,
            minHeight: 'auto',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '10px 0 10px 15px',
            '&[aria-selected="true"], &:hover, &[aria-selected="true"]:hover': {
                backgroundColor: 'transparent',
                color: colors.textPrimary
        },
      },
    },
    [`&.${autocompleteClasses.popperDisablePortal}`]: {
      position: 'relative',
    },
  }))


function PopperComponent(props) {
    const { disablePortal, anchorEl, open, ...other } = props
    const { colors } = usePaletteTheme()
    return <StyledAutocompletePopper colors={colors} {...other}/>
}

export default function AutocomplateButton({options = [], value, setValue, name, allKey = 'all', children, shouldHaveAll}) {
    const [anchorEl, setAnchorEl] = useState(null)
    const [fullOptions, setFullOptions] = useState(options)

    useEffect(() => {setFullOptions(options);return () => {}},[options])

    const all = useMemo(() => fullOptions?.find((option) => option.value === allKey)?.value ,[fullOptions]) // eslint-disable-line
    const allOptions = useMemo(() => fullOptions.filter((option) => option.value !== all).map(option => option.value), [fullOptions, all])

    useEffect(() => {
        shouldHaveAll && setFullOptions(all? options : [{name: capitalize(allKey), value: allKey},...options])
        return () => {}
    } ,[shouldHaveAll, options]) // eslint-disable-line

    const defaultValue = useMemo(() => {
        const dValue = options?.find(option => option.default)?.value
        if(!dValue) return []
        return all && dValue.includes(all) ? allOptions : [dValue]
    },[options, all]) // eslint-disable-line

    useEffect(() => {(!anchorEl && (!value.length || value.includes(all))) && setValue(defaultValue);return () => {}} ,[defaultValue, value]) // eslint-disable-line

    const handleClick = (event) => setAnchorEl(event.currentTarget)
     
    const handleClose = () => {
        defaultValue && !value.length && setValue(defaultValue)
        setAnchorEl(null)
    }

    return (
        <>
            {cloneElement(children, {onClick:handleClick})}
            <PopoverMenu
                anchorEl={anchorEl}
                onClose={handleClose}>
                 <Dropdown handleClose={handleClose} value={value} setValue={setValue} options={fullOptions} allKey={allKey} allOptions={allOptions}/>
            </PopoverMenu>
        </>

    )
}

const Dropdown = forwardRef(({handleClose, value, setValue, options, allKey, allOptions}, ref) => {
    const [ allActive, setAllActive ] = useState(false)
    const [ inputValue, setInputValue ] = useState("")

    useEffect(() => {setAllActive(allOptions.length === value.length);return () => {}},[value, allOptions])

    const onClose = (_, reason) => reason === 'escape' && handleClose()

    const onChange = (event, newValue, reason) => {
        if(event.type === 'keydown' && event.key === 'Backspace' && reason === 'removeOption') return
        const update = newValue.map(newVal => newVal.value)
        const handleAll = update.includes(allKey) 
            ? allActive
                ? []
                : allOptions
            : update
        setValue(handleAll)
    }

    const renderTags = () => null

    const checkIfSelected = (val) => autocompleteValue.find(selected =>  selected.value === val)
    
    const renderOption = (props, option) => {
        const isActive = !!checkIfSelected(option.value) || allActive
        props['aria-selected'] = isActive
        return (<li {...props} key={`${option}_${props.id}`}>
                <div className={isActive ?styles['dropdown-active'] : ''}> {option.name} </div>
                <Done color='success' style={{visibility: isActive ? 'visible' : 'hidden'}}/>
        </li>)
    }

    const getItem = useCallback((val) => options.find(option => option.value === val),[options])
    const getOptionLabel = (option) => option.name

    const autocompleteValue = useMemo(() => value?.map(val => getItem(val)),[value,getItem])

    const onChangeInput = (e) => setInputValue(e.target.value)

    return (
      <div className={styles['dropdown']}>
        <Autocomplete
            ref={ref}
            open
            fullWidth
            multiple
            onClose={onClose}
            value={autocompleteValue}
            onChange={onChange}
            disableCloseOnSelect
            PopperComponent={PopperComponent}
            renderTags={renderTags}
            noOptionsText="No options"
            renderOption={renderOption}
            getOptionLabel={getOptionLabel}
            options={options}
            inputValue={inputValue}
            renderInput={(params) => (
                <>
                    <InputBase
                        ref={params.InputProps.ref}
                        classes={{ root: styles['input']}}
                        onChange={onChangeInput}
                        startAdornment={<Search color="icon"/>}
                        inputProps={params.inputProps}
                        autoFocus
                        placeholder="Search"
                    />
                    <Divider classes={{root: styles['divier-root']}}/>
                </>
            )}
        />
      </div>
  )
})
